import React, {
    FC,
    useEffect,
    useState,
} from 'react';

import { Button, message } from 'antd';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { PUBLIC_ASSETS } from '@/consts';
import Spinner from '../../Common/Spin';
import FilesUploaderDragger from '../../Common/FilesUploaderDragger';
import { UploadingPopup } from '../../Common/OverflowWidget';
import SuccessScreen from './SuccessScreen/SuccessScreen';
import MessageTextBlock from './MessageTextBlock';
import { PublicFooter } from '../../Common/PublicComponents';
import {
    captureSharedFileFlowError,
    handleNotFound,
    Header,
    OTPAuth,
    ErrorResult,
    tryFetchActualPolicy,
    useRecipientEmail,
    getPolicyLanguage,
    MAIN_SPINNER_STYLE,
    useURLLinkId,
    useLanguageDefinition,
    getDefaultErrorResultProps,
} from '../Common';
import { useMounted, useStateWithRef, useStores } from '../../hooks';
import { CheckCodeError } from '../../Common/CodeAuthenticator';
import {
    EntityLoadErrorType,
    LinkAuthFlow,
    LinkDetails,
} from '@/types/types';
import {
    FileSharingParams,
} from './interfaces';
import config from '../../../config/env';
import { BRANDING } from '@/config/branding';
import {
    BASEURL, ENDPOINTS, addPermissionsToFile,
} from '../../../api';
import { getDebugInfo } from '../../../stores';
import {
    authFlowToRequirements,
    checkIsMFAFailedError,
    checkIsMFARequiredError,
    PolicyAggregate,
} from '../../utils';
import {
    errorToErrorType, resolveCleartextSenderEmail, validateAccess, verifyAccess,
} from './helpers';
import './index.scss';

interface SharedWorkspaceProps {
    isRootLoading: boolean;
}

const nameSpace = 'recipientPage.workspace';

const { DEFAULT_LANGUAGE } = config;

const SharedWorkspace: FC<SharedWorkspaceProps> = observer(({
    isRootLoading,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [errorType, setErrorType] = useState<EntityLoadErrorType>(null);
    const [showOTP, setShowOTP] = useState<boolean>(false);
    const [errorObject, setErrorObject] = useState<unknown>(null);

    const {
        authSettingsStore, uploadFilesStore, userStore,
    } = useStores();

    const { recpEmail, setInitRecipientEmail } = useRecipientEmail(userStore);

    // Verification requirements come from the PII-free auth-flow endpoint so the
    // OTP gate can render pre-auth without fetching file metadata.
    const [authRequirements, setAuthRequirements] = useState<PolicyAggregate>({});

    const { t } = useTranslation();
    const { isLanguageDefined, defineLanguage } = useLanguageDefinition();

    const { API, skipMFA, HAS_API_PROXY } = authSettingsStore;
    const { uploadingPopupOpened, setUploadingPopupOpened } = uploadFilesStore;
    const { takeWorkspaceFilesOwnership } = uploadFilesStore;

    const [
        fileSharingParams,
        setFileSharingParams,
        fileSharingParamsRef,
    ] = useStateWithRef<FileSharingParams>(null);

    const isMountedRef = useMounted();

    useEffect(() => {
        // AppAuthenticator already calls configureAmplify() unconditionally
        // for the whole subtree, so the duplicate call that used to live here
        // was a no-op. Keeping the effect only for the cleanup below.
        return () => {
            uploadFilesStore.clear();
            setUploadingPopupOpened(false);
        };
    }, []);

    useURLLinkId((linkId) => setFileSharingParams({ linkId }));

    const isReadyToLoadData = !!fileSharingParams && !isRootLoading;

    const getLinkDetails = async (linkId: string): Promise<LinkDetails> => {
        if (!linkId) {
            return null;
        }
        const getLinkCallback = async (): Promise<LinkDetails> => (
            API.get(BASEURL.backend(), ENDPOINTS.getLinkDetails(linkId), {})
        );
        return handleNotFound<LinkDetails>(getLinkCallback);
    };

    const getAuthFlow = async (linkId: string): Promise<LinkAuthFlow> => {
        if (!linkId) {
            return null;
        }
        const getAuthFlowCallback = async (): Promise<LinkAuthFlow> => (
            API.get(BASEURL.backend(), ENDPOINTS.getLinkAuthFlow(linkId), {})
        );
        return handleNotFound<LinkAuthFlow>(getAuthFlowCallback);
    };

    // Post-auth only: resolve the file policy via the authenticated endpoint and
    // apply the policy language now that the recipient is verified.
    const tryFetchPolicySettings = async (fileId: string): Promise<void> => {
        const filePolicy = await tryFetchActualPolicy(API, fileId, null);
        if (filePolicy && !isLanguageDefined) {
            const language = getPolicyLanguage(filePolicy) || DEFAULT_LANGUAGE;
            defineLanguage(language);
        }
    };

    const tryResolveSenderEmail = async (linkId: string, publicSenderEmail?: string): Promise<void> => {
        const senderEmail = await resolveCleartextSenderEmail(API, linkId, publicSenderEmail, HAS_API_PROXY);
        if (isMountedRef.current && senderEmail) {
            setFileSharingParams((currentState) => ({ ...currentState, srcEmail: senderEmail }));
        }
    };

    const fetchPageData = async (): Promise<void> => {
        const { linkId } = fileSharingParams;
        try {
            const linkDetails: LinkDetails = await getLinkDetails(linkId);
            setInitRecipientEmail(linkDetails?.recp_email);
            const fileId = linkDetails?.file_id;
            const srcEmail = linkDetails?.src_email;
            // Pre-auth we only load PII-free link + auth-flow metadata; file
            // details/policy are deferred until the recipient authenticates.
            const authFlow = await getAuthFlow(linkId);
            setAuthRequirements(authFlowToRequirements(authFlow));
            setFileSharingParams((currentState) => ({ ...currentState, fileId, srcEmail }));
            if (userStore.isUserSetUp) {
                tryResolveSenderEmail(linkId, srcEmail);
                await tryFetchPolicySettings(fileId);
                await validateAccess(
                    API,
                    {
                        linkId,
                        fileId,
                        email: userStore.currentUserEmail,
                    },
                    setErrorObject,
                );
            } else {
                if (!isLanguageDefined) {
                    defineLanguage();
                }
                setShowOTP(true);
            }
        } catch (error) {
            console.log('error is', error);
            if (isMountedRef.current) {
                if (!isLanguageDefined) {
                    defineLanguage();
                }
                if (!skipMFA && checkIsMFARequiredError(error)) {
                    setShowOTP(true);
                } else {
                    captureSharedFileFlowError(error, 'SharedWorkspace.fetchPageData');
                    setErrorType(errorToErrorType(error));
                }
            }
        }
        setIsLoading(false);
    };

    useEffect(() => {
        if (isReadyToLoadData) {
            fetchPageData();
        }
    }, [isReadyToLoadData]);

    const closeOTP = (): void => {
        setShowOTP(false);
    };

    const onOTPSuccess = async (): Promise<void> => {
        closeOTP();
        setIsLoading(true);
        try {
            const { fileId, linkId, srcEmail } = fileSharingParamsRef.current;
            tryResolveSenderEmail(linkId, srcEmail);
            // Now authenticated: fetch the deferred file policy (authenticated
            // endpoint) and apply its language before granting permissions.
            await tryFetchPolicySettings(fileId);
            await addPermissionsToFile(API, { fileId, linkId, email: userStore.currentUserEmail }, setErrorObject);
        } catch (error) {
            setErrorType(errorToErrorType(error));
        }
        setIsLoading(false);
    };

    const onClickShare = async (): Promise<void> => {
        const { successfullyUploadedItems, isUploading } = uploadFilesStore;
        if (successfullyUploadedItems.length && !isUploading) {
            setIsLoading(true);
            const success = await takeWorkspaceFilesOwnership(fileSharingParamsRef.current.fileId);
            if (success) {
                setUploadingPopupOpened(false);
                setIsSuccess(true);
            } else {
                message.error(t('general.specterxCommon.couldNotShareTryAgain'));
            }
        } else {
            message.info(isUploading ? t('shareBlockers.isUploading') : t('shareBlockers.noFiles'));
        }
        setIsLoading(false);
    };

    const sourceEmail = fileSharingParams?.srcEmail || '';

    const verifyAccessInMFAFlow = async (code: string): Promise<void> => {
        try {
            await verifyAccess(API, fileSharingParamsRef.current.fileId, code);
        } catch (error) {
            if (checkIsMFAFailedError(error)) {
                throw new CheckCodeError('Code check failed');
            } else {
                throw error;
            }
        }
    };

    return (
        <Spinner
            size="large"
            style={MAIN_SPINNER_STYLE}
            spinning={isLoading}
        >
            <Header showSkeleton={!isLanguageDefined} />
            {errorType ? (
                <ErrorResult
                    {...getDefaultErrorResultProps(
                        t,
                        errorType,
                        'general.specterxCommon.workspace',
                        sourceEmail,
                        getDebugInfo(errorObject, {
                            linkId: fileSharingParams?.linkId,
                            workspaceId: fileSharingParams?.fileId,
                            email: userStore?.currentUserEmail,
                            userId: userStore?.currentUserId,
                        }),
                    )}
                />
            )
                : (
                    <>
                        {userStore.isUserSetUp && !showOTP && (
                            <div className="workspace-content-wrapper">
                                <img
                                    className="workspace-img"
                                    src={`${PUBLIC_ASSETS}/images/shared-workspace.svg`}
                                    alt="Shared workspace"
                                />
                                {!isSuccess ? (
                                    <>
                                        <div className="title">
                                            {t(`${nameSpace}.shareFilesWith`, { ownerEmail: sourceEmail })}
                                        </div>
                                        <MessageTextBlock source={sourceEmail} />
                                        <div className="files-uploader-workspace-wrapper">
                                            <FilesUploaderDragger
                                                drawerState="upload"
                                                isSidebar={false}
                                                sharedWorkspace
                                                folderId={fileSharingParams?.fileId}
                                                directory={false}
                                            />
                                        </div>
                                        <Button className="share-securely-button" onClick={onClickShare}>
                                            {BRANDING !== 'unbranded' && (
                                                <img
                                                    src={`${PUBLIC_ASSETS}/company_logo/specterx-icon.png`}
                                                    alt=""
                                                    width={30}
                                                />
                                            )}
                                            <div>{t(`${nameSpace}.shareSecurely`)}</div>
                                        </Button>
                                    </>
                                ) : (<SuccessScreen sourceEmail={sourceEmail} />)}
                            </div>
                        )}
                        {showOTP
                            && (
                                <div className="otp-auth-container">
                                    <OTPAuth
                                        recpEmail={recpEmail}
                                        OTPType="workspace"
                                        setIsLoading={setIsLoading}
                                        closeOTP={closeOTP}
                                        onSuccess={onOTPSuccess}
                                        policyAggregate={authRequirements}
                                        openFile={verifyAccessInMFAFlow}
                                        onMFASuccess={closeOTP}
                                        hasBackButton={false}
                                    />
                                </div>
                            )}
                    </>
                )}
            <PublicFooter showSkeleton={!isLanguageDefined} />
            {uploadingPopupOpened && <UploadingPopup />}
        </Spinner>
    );
});

export default SharedWorkspace;
